import type { BenLanguageCode } from '@ben/common/languages';
import { BEN_LANGUAGES } from '@ben/common/languages';
import RiEarthLine from '@ben/icons/earth-line';
import MantineTooltip from '@ben/orion/mantine-tooltip';
import Menu from '@ben/orion/menu';
import RIcon from '@ben/orion/remix-icon';
import type { MenuProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LeftNavigationActionIcon } from '../common/action-icon';

interface LanguageMenuProps extends Omit<MenuProps, 'onChange'> {
  value?: BenLanguageCode;
  onChange?: (value: BenLanguageCode) => void;
}

const LanguageMenu = ({
  value = 'en',
  onChange,
  ...rest
}: LanguageMenuProps) => {
  const { t } = useTranslation();
  const [opened, { toggle }] = useDisclosure();

  if (!onChange) {
    return;
  }

  return (
    <Menu
      {...rest}
      position="bottom-start"
      opened={opened}
      onChange={toggle}
      width="282px"
      middlewares={{ size: true }}
      floatingStrategy="fixed"
    >
      <MantineTooltip
        label={t('leftNavigation.language')}
        disabled={opened}
        position="bottom-start"
      >
        <Menu.Target data-testid="language-target">
          <LeftNavigationActionIcon
            aria-label={t('leftNavigation.language')}
            $menuOpened={opened}
          >
            <RIcon icon={RiEarthLine} size="md" />
          </LeftNavigationActionIcon>
        </Menu.Target>
      </MantineTooltip>
      <LanguageMenuDropdown>
        {BEN_LANGUAGES.map((benLanguage, index) => {
          const isLast = index === BEN_LANGUAGES.length - 1;
          const benLanguageCode = benLanguage.code;
          return (
            <Fragment key={benLanguageCode}>
              <Menu.Item
                value={benLanguageCode}
                onClick={() => onChange(benLanguageCode)}
                active={value === benLanguageCode}
              >
                {benLanguage.label}
              </Menu.Item>
              {!isLast && <Menu.Divider />}
            </Fragment>
          );
        })}
      </LanguageMenuDropdown>
    </Menu>
  );
};

export default LanguageMenu;

const LanguageMenuDropdown = styled(Menu.Dropdown)`
  && {
    overflow-y: auto;
  }
`;
